import {Card, Container, Group, Menu, rem, Space, Tabs, Text, Title, UnstyledButton} from "@mantine/core";
import {useLocation, useNavigate} from "react-router-dom";
import {lazy, PropsWithChildren, Suspense, useCallback, useMemo} from "react";
import {PersonEntity, personType} from "@ots-frontend/ots-data";
import {PersonDetails} from "./PersonDetails";
import {createContentStyles} from "../../../theme/useContentStyles";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {PersonAction, PersonActionMenuDropdown} from "../../menu/PersonActionMenu/PersonActionMenu";
import {usePersonMutation} from "../../../hooks/usePersonMutation";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {ObjectChangeLogView} from "../common/ObjectChangeLogView/ObjectChangeLogView";
import {Badge} from "../../badge/Badge/Badge";
import {TabItem} from "../../tabs/Tabs/Tabs";
import {PersonDVIRView} from "./PersonDVIRView";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";

const NotificationSubscriptionsPageRoute = lazy(() =>
  import("@ots-frontend/ots-ui-tw").then(module => ({
    default: module.NotificationSubscriptionsPageRoute
  }))
);

const useStyles = createContentStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type PersonProfileProps = PropsWithChildren<{
  person: PersonEntity
}>

export const PersonProfile = (props: PersonProfileProps) => {
  const {person} = props;

  const {classes} = useStyles();

  const {t} = useTranslation()
  const {user, hasAccess} = useAuth();
  const isSuperAdmin = user?.roles?.some(role => role.key === "SUPER_ADMIN") ?? false;
  const canManageNotificationSubscriptions = isSuperAdmin || hasAccess("notification_subscription_manage");
  const navigate = useNavigate();
  const {pathname, search, state} = useLocation();

  const {
    deletePerson
  } = usePersonMutation()

  const onActionClick = useCallback((action: PersonAction) => {
    if (action === 'EDIT') {
      navigate(`/persons/form`, {state: {id: person.id}})
    } else if (action === 'DELETE') {
      deletePerson(person.id, { onSuccess: () => navigate(-1) })
    }
  }, [person, navigate, deletePerson])

  const tabs = useMemo(() => {
    const _tabs: TabItem[] = []

    _tabs.push({
      key: "details",
      value: "Details"
    })

    if (person.type === personType.driver) {
      _tabs.push({
        key: "dvirs",
        value: "DVIRs",
        permissions: ["dvir_view"]
      })
    }

    _tabs.push({
      key: "history",
      value: "Change Logs",
      permissions: ["change_log_view"]
    })

    if (canManageNotificationSubscriptions) {
      _tabs.push({
        key: "notifications",
        value: "Notifications"
      })
    }

    return _tabs
  }, [person, canManageNotificationSubscriptions])

  const items = tabs.map((tab) => (
    <HasAccess accessOnEmpty={true} permissions={tab.permissions} key={tab.key}>
      <Tabs.Tab value={tab.key}>
        {tab.value}
      </Tabs.Tab>
    </HasAccess>
  ));

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <Title order={4}>
            User: {person.name}
          </Title>

          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <PersonActionMenuDropdown person={person} onActionClick={onActionClick}/>
            </Menu.Dropdown>
          </Menu>
        </Group>

        {person.state?.inShift &&
          <Badge color="green">
            Active
          </Badge>
        }
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Tabs
            keepMounted={false}
            defaultValue={state?.tabValue ?? "details"}
            onTabChange={(value) => navigate(pathname + search, {replace: true, state: {...state, tabValue: value}})}
          >
            <Tabs.List>{items}</Tabs.List>
            <Space h="md"/>
            <Tabs.Panel value="details">
              <PersonDetails person={person}/>
            </Tabs.Panel>
            <Tabs.Panel value="dvirs">
              <PersonDVIRView person={person}/>
            </Tabs.Panel>
            <Tabs.Panel value="history">
              <ObjectChangeLogView entity={person}/>
            </Tabs.Panel>
            {canManageNotificationSubscriptions && (
              <Tabs.Panel value="notifications">
                <Suspense fallback={<Text size="sm" color="dimmed">Loading notification subscriptions…</Text>}>
                  <NotificationSubscriptionsPageRoute
                    personId={person.id}
                    personName={person.name}
                    density="compact"
                    embedded/>
                </Suspense>
              </Tabs.Panel>
            )}
          </Tabs>
        </Container>
      </Card.Section>
    </Card>
  );
};
